import { useState } from "react";
import { BlockProps, ContactDetailsBlockType } from "../types";
import { parseBlockInfo } from "../utils";
import { getBlockConfig } from "../constants";
import { useStateWithFallback } from "@tellescope/react-hooks";
import { useUncontrolledState } from "@tellescope/ui";
import {
  TellescopeModelFromName,
  usePermissionQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { useBlockMutations } from "./use-block-mutations";
import { useResolvedView } from "./use-resolved-view";

export interface UseBlockArgs<T extends ContactDetailsBlockType, S> extends BlockProps {
  type: T;
  select?: (data: TellescopeModelFromName<"endusers">) => S;
}

export const useBlock = <T extends ContactDetailsBlockType, S>({
  type,
  info,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  enduserId,
  select,
  viewId,
  blockId,
  blockIndex,
  blockLayout = "capped",
  onViewMaterialized,
}: UseBlockArgs<T, S>) => {
  const { updateInfo, duplicateBlock, deleteBlock, toggleExpanded } = useBlockMutations({
    type,
    viewId,
    blockId,
    blockIndex,
    onViewMaterialized,
  });

  const parsedInfo = parseBlockInfo<T>(type, info);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useStateWithFallback({
    initialValue: parsedInfo?.title,
    fallbackValue: getBlockConfig(type).getName(),
  });
  const [editMode, _setEditMode] = useUncontrolledState({
    state: providedEditMode ?? false,
    setState: providedSetEditMode,
  });
  const setEditMode = providedSetEditMode ? _setEditMode : undefined;

  const enduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
    enabled: !!enduserId,
    select,
  });

  const resolvedView = useResolvedView(viewId);

  const {
    update: { access: canEditBlock },
    delete: { access: canDeleteBlock },
    create: { access: canCreateBlock },
  } = usePermissionQuery({
    model: "custom_dashboards",
    actions: ["update", "delete", "create"],
    record: resolvedView.mode === "saved" ? resolvedView.view : undefined,
  });

  const canEditTitle = editMode && canEditBlock;

  const updateEnduserMutation = useTellescopeMutation({
    model: "endusers",
    method: "updateOne",
  });
  const {
    update: { access: canEditEnduser },
  } = usePermissionQuery({
    model: "endusers",
    actions: ["update"],
    recordId: enduserId,
  });

  return {
    parsedInfo,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    enduserQuery,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    blockLayout,
    expanded: blockLayout !== "capped" || (parsedInfo?.expanded ?? false),
    canEditTitle,
    canEditBlock,
    canDeleteBlock,
    canCreateBlock,
    updateEnduserMutation,
    canEditEnduser,
  };
};
